import React, { useState, useEffect, FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Group,
    Heading,
    InformationType,
    Level,
    Text,
    TextInput,
    TextInputMode,
    useTheme,
    Variant,
} from '@defa/defa-component-library';
import i18n from '@i18n';
import { useAuthState } from '../../auth';

import { BrandPanel } from '@fractions/brand-panel';
import { AuthLayout } from '@fractions/auth-layout';
import { humanizeError } from '@utils/error';
import { useNotification } from '@utils/notification';

export const Login: React.FunctionComponent = () => {
    const { isAuthenticated, login } = useAuthState();
    const navigate = useNavigate();
    const { msg } = useParams<{ msg: string }>();
    const theme = useTheme();

    const { add: addNotification } = useNotification();

    const [password, setPassword] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const valid = email && password;

    useEffect(() => {
        if (msg === 'invalidtokenoradminexist') {
            addNotification({
                message: i18n.t('LogIn.InvalidTokenOrAdminExist'),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [msg]);

    const submit = React.useCallback(
        async (event: FormEvent) => {
            event.preventDefault();
            if (valid) {
                try {
                    const loginOk = await login(email, password);
                    if (!loginOk) {
                        const message = i18n.t('LogIn.FailedMessage');
                        setErrorMessage(message);
                        addNotification({ message });
                    } else {
                        setErrorMessage(null);
                    }
                } catch (error) {
                    const message = humanizeError(error);
                    setErrorMessage(message);
                    addNotification({ message });
                }
            }
        },
        [addNotification, login, password, email, valid]
    );

    const forgotPasswordClick = React.useMemo(
        () => (): void => {
            navigate('/reset-password');
        },
        [navigate]
    );

    const onEmailChange = React.useMemo(
        () =>
            (text: string): void => {
                setEmail(text);
                setErrorMessage(null);
            },
        [setEmail]
    );

    const onPasswordChange = React.useMemo(
        () =>
            (text: string): void => {
                setPassword(text);
                setErrorMessage(null);
            },
        [setPassword]
    );

    if (isAuthenticated) {
        return <Navigate to="/" />;
    }

    return (
        <AuthLayout
            brand={
                <BrandPanel
                    heading={i18n.t('BrandPanel.Header')}
                    subHeading={i18n.t('BrandPanel.SubHeader')}
                />
            }
        >
            <Group form onSubmit={submit}>
                <Heading level={Level.h1}>{i18n.t('LogIn.Header')}</Heading>
                <Group>
                    <TextInput
                        label={i18n.t('LogIn.EmailLabel')}
                        name="email"
                        value={email}
                        onChange={onEmailChange}
                        mode={TextInputMode.EMAIL}
                        autocomplete="username"
                        informationType={InformationType.ERROR}
                        message={errorMessage ? ' ' : undefined}
                    />
                    <TextInput
                        label={i18n.t('LogIn.PasswordLabel')}
                        name="password"
                        mode={TextInputMode.PASSWORD}
                        value={password}
                        onChange={onPasswordChange}
                        autocomplete="password"
                        informationType={InformationType.ERROR}
                        message={errorMessage ? ' ' : undefined}
                    />
                    {errorMessage && <Text color={theme.errorColor}>{errorMessage}</Text>}
                    {!errorMessage && msg === 'invalidtokenoradminexist' && (
                        <Text>{i18n.t('LogIn.InvalidTokenOrAdminExistMessage')}</Text>
                    )}
                </Group>
                <Group>
                    <Button
                        text={i18n.t('LogIn.SubmitButton')}
                        disabled={!valid}
                        type="submit"
                        name="login-button"
                    />
                    <Button
                        text={i18n.t('LogIn.ForgotPassword')}
                        onClick={forgotPasswordClick}
                        variant={Variant.GRAYSCALE}
                        name="forgotten-password"
                    />
                </Group>
            </Group>
        </AuthLayout>
    );
};
